 
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AcadvModule, AcadvModuleMap } from './framework.model';

export interface AuthorizationModel {
  authorizationToken: string; // id_token
  cognitoToken: string; // access_token
  expiration: number;
  refresh_token: string;
}

interface IdentityModel {
  userId: string;
  providerName: string;
  providerType: string;
  issuer: string;
  primary: string;
  dateCreated: string;
}

export interface UserTokenModel {
  at_hash: string;
  sub: string;
  'cognito:groups': string[];
  email_verified: string;
  home: string;
  quickcalls: string;
  preverification: string;
  dreams: string;
  stipsanddocuments: string;
  iss: string;
  rehash: string;
  'cognito:username': string;
  given_name: string;
  aud: string;
  identities: IdentityModel[];
  token_use: string;
  auth_time: number;
  exp: number;
  iat: number;
  family_name: string;
  email: string;
  dealerno: string;
  ACA_Internal_User: string;
  preverification_read_only: string;
  dreams_read_only: string;
  rehash_read_only: string;
  rehash_b_flag?: boolean; // TODO: Must match name of Cognito claim
  /** AOS-5218: SFS Rehash — no dedicated Cognito claim yet; access granted to all authenticated users (tab visibility controlled by riskDealerPricingGroup) */
  rehash_sfs?: string;
  sfs_loan_id: string;
  isSfs: string;
  sso_provider?: string;
}

export type SsoProvider = 'Internal' | 'RouteOne' | 'Dealertrack' | 'External' | '';

export class User {
  firstName!: string;
  lastName!: string;
  fullName!: string;
  username!: string;
  home!: boolean;
  preverification!: boolean;
  dreams!: boolean;
  rehash!: boolean;
  /** AOS-5218: SFS Rehash access — granted to all authenticated users; tab visibility is controlled by riskDealerPricingGroup */
  rehashSfs!: boolean;
  rehashBTest!: boolean;
  dealerno!: number;
  isInternal!: boolean;
  email!: string;
  preverificationReadOnly!: boolean;
  dreamsReadOnly!: boolean;
  rehashReadOnly!: boolean;
  sfsLoanId!: string;
  isSfs!: boolean;
  ssoProvider?: SsoProvider;

  

  static fromToken(userToken: UserTokenModel, defaultInternalUserDealerNo: number) {
    const user = new User();
    user.firstName = userToken.given_name;
    user.lastName = userToken.family_name;
    user.fullName = `${userToken.given_name} ${userToken.family_name}`;
    user.username = userToken['cognito:username']; // dealer users = dealer SSO provided username, internal users = acadvantage-provider_<ACA email address>
    user.home = true;
    user.preverification = userToken.preverification === 'true';
    user.dreams = userToken.dreams === 'true';
    user.rehash = userToken.rehash === 'true';
    // AOS-5218: Grant rehash-sfs access to all authenticated users.
    // Tab visibility is already controlled by riskDealerPricingGroup on the dealer.
    // A dedicated Cognito claim can be added later if finer-grained control is required.
    user.rehashSfs = true;
    user.dealerno = +userToken.dealerno > 0 ? +userToken.dealerno : 0;
    user.isInternal = userToken.ACA_Internal_User === 'true';
    user.email = userToken.email; // not typically available for dealer users
    user.preverificationReadOnly = userToken.preverification_read_only === 'true';
    user.dreamsReadOnly = userToken.dreams_read_only === 'true';
    user.rehashReadOnly = userToken.rehash_read_only === 'true';
    user.rehashBTest = userToken.rehash_b_flag ?? false; // if claim not provided, do not assume B test in rehash
    user.sfsLoanId = userToken.sfs_loan_id || '';
    user.isSfs = userToken.isSfs === 'true';
    user.ssoProvider = (userToken.sso_provider as SsoProvider) || '';

    if (user.isInternal && user.dealerno == 0) {
      try {
        const historyItems = JSON.parse(localStorage.getItem('selectedDealerHistory') || '[]') as any[];
        user.dealerno = +historyItems[0].dealerNumber;
      } catch {
        user.dealerno = defaultInternalUserDealerNo; // A 'starter' dealer number for internal users
      }
    }
    if (user.isInternal) {
      user.ssoProvider = 'Internal';
    }
    //user.preverification = false; // FOR TESTING
    return user;
  }

  getAccessibleModules() {
    const modules: AcadvModule[] = [];

    Object.keys(AcadvModuleMap).forEach((key) => {
      if ((this as any)[key] == true) {
        modules.push(AcadvModuleMap[key]);
      }
    });

    return modules;
  }
}

/** True when the JWT carries a locked SFS loan (`isSfs` + `sfs_loan_id`). */
export function isSfsLockedLoanUser(
  user: User | undefined
): user is User & { isSfs: true; sfsLoanId: string } {
  return !!(user?.isSfs && user.sfsLoanId);
}

export interface OriginUser {
  dealerNo: number;
  lastName: string;
  userName: string;
  firstName: string;
  acaInternalUser: boolean;
}

export interface RefreshTokenResponseModel {
  body: {
    access_token: string;
    expires_in: number;
    id_token: string;
    token_type: string;
  };
  statusCode: 200;
}
